import { useState } from 'react';
import { Card, Tabs, Button, Badge, Modal, Input, Select } from '@getpay93/ui';
import { useClientView, useMyProofs, useUploadProof } from '../../data/hooks';
import { compressImageToBase64 } from '../../lib/compressImage';
import { money, shortDate } from '../../lib/format';
import { MyProofImage } from './MyProofImage';

export function Pagos() {
  const view = useClientView();
  const proofs = useMyProofs();
  const upload = useUploadProof();
  const [tab, setTab] = useState<'pagar' | 'historial'>('pagar');
  const [invoiceId, setInvoiceId] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('transferencia');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pendings = (view.data?.invoices ?? []).filter((i) => i.status === 'pending');

  function openFor(id: string, amt: number) { setInvoiceId(id); setAmount(String(amt)); setFile(null); setError(null); }

  async function send() {
    if (!file) { setError('Elige una imagen del comprobante.'); return; }
    setBusy(true);
    setError(null);
    try {
      const { base64, mime } = await compressImageToBase64(file);
      await upload.mutateAsync({ invoiceId, amount: Number(amount), paymentMethod: method, imageBase64: base64, imageMime: mime });
      setInvoiceId('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo enviar el comprobante');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Tabs tabs={[{ id: 'pagar', label: 'Por pagar' }, { id: 'historial', label: 'Historial' }]} value={tab} onChange={(id) => setTab(id as 'pagar' | 'historial')} />

      {tab === 'pagar' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {pendings.map((i) => (
            <Card key={i.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 800, color: 'var(--text-strong)' }}>{money(i.amount)}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-hint)' }}>Vence {shortDate(i.dueDate)}</div>
                </div>
                <Button size="sm" onClick={() => openFor(i.id, i.amount)}>Subir comprobante</Button>
              </div>
            </Card>
          ))}
          {pendings.length === 0 ? <Card><p style={{ margin: 0, color: 'var(--text-hint)' }}>Sin pagos pendientes.</p></Card> : null}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(proofs.data ?? []).map((p) => (
            <Card key={p.id}>
              <div style={{ display: 'flex', gap: 12 }}>
                <MyProofImage proofId={p.id} style={{ width: 64, height: 64, borderRadius: 8, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 700, color: 'var(--text-strong)' }}>{money(p.amount)}</span>
                    <Badge tone={p.status === 'approved' ? 'paid' : p.status === 'rejected' ? 'overdue' : 'pending'}>{p.status === 'pending_review' ? 'En revisión' : p.status === 'approved' ? 'Aprobado' : 'Rechazado'}</Badge>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-hint)' }}>{shortDate(p.paidAt) || shortDate(p.createdAt)}</div>
                  {p.status === 'rejected' && p.reviewNote ? <div style={{ fontSize: 12, color: 'var(--status-overdue)', marginTop: 4 }}>{p.reviewNote}</div> : null}
                </div>
              </div>
            </Card>
          ))}
          {proofs.data && proofs.data.length === 0 ? <Card><p style={{ margin: 0, color: 'var(--text-hint)' }}>Sin comprobantes aún.</p></Card> : null}
        </div>
      )}

      {invoiceId ? (
        <Modal open onClose={() => setInvoiceId('')} title="Subir comprobante" footer={<><Button variant="ghost" onClick={() => setInvoiceId('')}>Cancelar</Button><Button disabled={busy || !file} onClick={send}>{busy ? 'Enviando…' : 'Enviar comprobante'}</Button></>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>Imagen del comprobante
              <input aria-label="Imagen del comprobante" type="file" accept="image/*" onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)} style={{ display: 'block', marginTop: 6, color: 'var(--text-body)' }} />
            </label>
            {file ? <span style={{ fontSize: 12, color: 'var(--text-hint)' }}>{file.name}</span> : null}
            <Input label="Monto" type="number" value={amount} onChange={(e) => setAmount(e.currentTarget.value)} />
            <Select label="Método" options={['transferencia', 'efectivo', 'deuna', 'otro']} value={method} onChange={(e) => setMethod(e.currentTarget.value)} />
            {error ? <p style={{ margin: 0, color: 'var(--status-overdue)', fontSize: 13, fontWeight: 600 }}>{error}</p> : null}
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
